import React from 'react';
import { Plus, Minus, Brain, Ear, EarOff } from 'lucide-react';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from './ui/select';
import { SOUND_TYPES } from '../lib/options';

const POLY_PRESETS = [
  { id: '3-2', a: 2, b: 3, label: '3:2', desc: 'Hemiola' },
  { id: '3-4', a: 4, b: 3, label: '3:4', desc: 'Triple over Quattro' },
  { id: '4-3', a: 3, b: 4, label: '4:3', desc: 'Quattro over Triple' },
  { id: '5-3', a: 3, b: 5, label: '5:3' },
  { id: '5-4', a: 4, b: 5, label: '5:4' },
  { id: '7-4', a: 4, b: 7, label: '7:4' },
  { id: '7-8', a: 8, b: 7, label: '7:8' },
];

const STATE_DOT = (s) =>
  s === 0 ? 'bg-white/10' : s === 2 ? 'bg-amber-400' : 'bg-amber-500/70';

// Voice B beat cell with amber styling to differentiate from voice A (cyan/rose)
function VoiceBCell({ index, state, isCurrent, onCycle }) {
  const stateCls = state === 0
    ? 'bg-[#0f0f10] border-white/10 hover:border-white/30 text-white/30'
    : state === 2
      ? 'bg-amber-500/10 border-amber-400/70 text-amber-300 shadow-[0_0_22px_rgba(251,191,36,0.35)] hover:bg-amber-500/20'
      : 'bg-amber-500/5 border-amber-500/40 text-amber-200 shadow-[0_0_14px_rgba(251,191,36,0.2)] hover:bg-amber-500/15';
  const playhead = isCurrent ? 'ring-2 ring-white scale-[1.06] brightness-150 z-10' : '';

  return (
    <button
      type="button"
      data-testid={`voiceB-cell-${index}`}
      aria-label={`Voce B - colpo ${index + 1}, stato ${state}`}
      onClick={() => onCycle(index)}
      className={`relative h-12 md:h-14 w-full rounded-md border transition-all duration-100 flex flex-col items-center justify-center cursor-pointer select-none focus-visible:ring-2 focus-visible:ring-amber-400 focus-visible:outline-none ${stateCls} ${playhead}`}
    >
      <span className="font-bebas text-xl md:text-2xl leading-none tabular-nums">{index + 1}</span>
      <span className="text-[9px] tracking-widest uppercase opacity-70">
        {state === 0 ? 'off' : state === 2 ? 'acc' : 'on'}
      </span>
    </button>
  );
}

export default function PolyrhythmPanel({
  beatsA,
  patternB,
  soundTypeB,
  currentBeatB,
  isPlaying,
  bpm,
  onSetPolyPreset,
  onSetBeatsB,
  onCycleBeatB,
  onSetSoundTypeB,
  // Trainer
  trainerEnabled,
  trainerPlayBars,
  trainerMuteBars,
  totalBarsPlayed,
  onTrainerToggle,
  onTrainerPlayBarsChange,
  onTrainerMuteBarsChange,
}) {
  const beatsB = patternB.length;
  const ratio = `${beatsB}:${beatsA}`;

  // Trainer phase: PLAY (voice B audible) or FEEL (voice B muted)
  let trainerPhase = null;
  if (trainerEnabled && trainerPlayBars > 0 && trainerMuteBars > 0 && totalBarsPlayed >= 0 && isPlaying) {
    const cycleLen = trainerPlayBars + trainerMuteBars;
    const phaseBar = totalBarsPlayed % cycleLen;
    const isMute = phaseBar >= trainerPlayBars;
    const barsLeft = isMute ? cycleLen - phaseBar : trainerPlayBars - phaseBar;
    trainerPhase = { isMute, barsLeft };
  }

  return (
    <section
      data-testid="polyrhythm-panel"
      className="bg-[#111112] border border-white/5 rounded-2xl p-6 md:p-8 shadow-xl"
    >
      <div className="flex items-center justify-between mb-5 flex-wrap gap-3">
        <div>
          <div className="text-xs uppercase tracking-[0.25em] text-white/50">
            Voce B · polyritmo <span className="text-amber-300">{ratio}</span>
          </div>
          <div className="font-bebas text-2xl tracking-wide">
            {beatsB} colpi distribuiti uniformemente nella stessa battuta di voce A ({beatsA})
          </div>
        </div>

        <div className="flex items-center gap-3 flex-wrap">
          <div className="flex items-center gap-2">
            <span className="text-[10px] uppercase tracking-widest text-white/50">Colpi B</span>
            <button
              data-testid="voiceB-decrement-btn"
              onClick={() => onSetBeatsB(beatsB - 1)}
              disabled={beatsB <= 1}
              className="h-9 w-9 rounded-md border border-white/10 hover:border-white/30 hover:bg-white/5 flex items-center justify-center disabled:opacity-30"
              aria-label="Riduci colpi B"
            >
              <Minus className="w-3.5 h-3.5" />
            </button>
            <span data-testid="voiceB-count" className="font-bebas text-2xl text-amber-300 tabular-nums w-7 text-center">{beatsB}</span>
            <button
              data-testid="voiceB-increment-btn"
              onClick={() => onSetBeatsB(beatsB + 1)}
              disabled={beatsB >= 16}
              className="h-9 w-9 rounded-md border border-white/10 hover:border-white/30 hover:bg-white/5 flex items-center justify-center disabled:opacity-30"
              aria-label="Aumenta colpi B"
            >
              <Plus className="w-3.5 h-3.5" />
            </button>
          </div>

          <div className="flex items-center gap-2">
            <span className="text-[10px] uppercase tracking-widest text-white/50">Suono B</span>
            <Select value={soundTypeB} onValueChange={onSetSoundTypeB}>
              <SelectTrigger data-testid="voiceB-sound-trigger" className="bg-[#0A0A0A] border-white/10 text-white h-9 w-32 font-bebas text-base tracking-wide">
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="bg-[#111112] border-white/10 text-white">
                {SOUND_TYPES.map((s) => (
                  <SelectItem key={s.id} value={s.id} data-testid={`voiceB-sound-${s.id}`} className="font-bebas text-base focus:bg-amber-500/20 focus:text-amber-200">
                    {s.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>
      </div>

      <div className="flex flex-wrap gap-2 mb-5">
        <span className="text-[10px] uppercase tracking-widest text-white/50 self-center mr-1">Preset:</span>
        {POLY_PRESETS.map((preset) => (
          <button
            key={preset.id}
            data-testid={`poly-preset-${preset.id}-btn`}
            onClick={() => onSetPolyPreset(preset.a, preset.b)}
            className={`h-9 px-3 rounded-md border text-xs uppercase tracking-widest font-mono transition-all ${
              beatsA === preset.a && beatsB === preset.b
                ? 'border-amber-400 bg-amber-500/15 text-amber-200'
                : 'border-white/10 hover:border-white/30 text-white/70'
            }`}
            title={preset.desc || preset.label}
          >
            {preset.label}
          </button>
        ))}
      </div>

      <div data-testid="voiceB-grid" className="grid gap-2 md:gap-3" style={{ gridTemplateColumns: `repeat(${beatsB}, minmax(0, 1fr))` }}>
        {patternB.map((state, i) => (
          <VoiceBCell
            key={i}
            index={i}
            state={state}
            isCurrent={isPlaying && currentBeatB === i}
            onCycle={onCycleBeatB}
          />
        ))}
      </div>

      <div className="flex flex-wrap items-center gap-4 mt-5 text-xs uppercase tracking-widest text-white/50">
        <span className="flex items-center gap-2">
          <span className="w-3 h-3 rounded-sm bg-amber-400 shadow-[0_0_10px_rgba(251,191,36,0.6)]" /> Voce B accent
        </span>
        <span className="flex items-center gap-2">
          <span className="w-3 h-3 rounded-sm bg-amber-500/60 shadow-[0_0_10px_rgba(251,191,36,0.4)]" /> Voce B click
        </span>
        <span className="flex items-center gap-2">
          <span className="w-3 h-3 rounded-sm border border-white/20" /> Off
        </span>
        <span className="ml-auto text-white/40 normal-case tracking-normal">
          Voce B: tono più grave per distinguersi
        </span>
      </div>

      {/* Rhythm Trainer */}
      <div data-testid="trainer-section" className="mt-6 pt-5 border-t border-white/5">
        <div className="flex items-center justify-between mb-3 flex-wrap gap-3">
          <div className="flex items-center gap-3">
            <Brain className="w-4 h-4 text-amber-400" />
            <div>
              <div className="text-xs uppercase tracking-[0.25em] text-white/50">
                Rhythm Trainer
              </div>
              <div className="font-bebas text-xl tracking-wide">
                Mute voce B · alterna ascolto e immaginazione
              </div>
            </div>
          </div>
          <label className="flex items-center gap-2 cursor-pointer">
            <input
              data-testid="trainer-toggle"
              type="checkbox"
              checked={trainerEnabled}
              onChange={(e) => onTrainerToggle(e.target.checked)}
              className="accent-amber-400 w-4 h-4"
            />
            <span className="text-xs uppercase tracking-widest text-white/60">
              {trainerEnabled ? 'Attiva' : 'Disattiva'}
            </span>
          </label>
        </div>

        <div className={`grid grid-cols-2 gap-3 ${trainerEnabled ? '' : 'opacity-50 pointer-events-none'}`}>
          <label className="flex flex-col gap-1">
            <span className="text-[10px] uppercase tracking-widest text-white/50 flex items-center gap-1.5">
              <Ear className="w-3 h-3" /> Play · bar
            </span>
            <input
              data-testid="trainer-play-input"
              type="number"
              min={1}
              max={32}
              value={trainerPlayBars}
              onChange={(e) => onTrainerPlayBarsChange(Math.max(1, Math.min(32, parseInt(e.target.value, 10) || 1)))}
              className="h-10 bg-[#0A0A0A] border border-white/10 rounded-md px-2 font-bebas text-xl text-center tabular-nums"
            />
          </label>
          <label className="flex flex-col gap-1">
            <span className="text-[10px] uppercase tracking-widest text-white/50 flex items-center gap-1.5">
              <EarOff className="w-3 h-3" /> Feel · bar
            </span>
            <input
              data-testid="trainer-mute-input"
              type="number"
              min={1}
              max={32}
              value={trainerMuteBars}
              onChange={(e) => onTrainerMuteBarsChange(Math.max(1, Math.min(32, parseInt(e.target.value, 10) || 1)))}
              className="h-10 bg-[#0A0A0A] border border-white/10 rounded-md px-2 font-bebas text-xl text-center tabular-nums"
            />
          </label>
        </div>

        {trainerPhase && (
          <div
            data-testid="trainer-phase-indicator"
            className={`mt-4 px-4 py-3 rounded-lg border font-bebas text-xl tracking-widest text-center transition-all ${
              trainerPhase.isMute
                ? 'bg-amber-500/15 border-amber-400/50 text-amber-200 shadow-[0_0_20px_rgba(251,191,36,0.25)]'
                : 'bg-pink-500/10 border-pink-400/40 text-pink-200'
            }`}
          >
            {trainerPhase.isMute ? (
              <>
                <EarOff className="w-5 h-5 inline mr-2 -mt-1" />
                FEEL IT · {trainerPhase.barsLeft} {trainerPhase.barsLeft === 1 ? 'bar' : 'bar'}
              </>
            ) : (
              <>
                <Ear className="w-5 h-5 inline mr-2 -mt-1" />
                LISTEN · {trainerPhase.barsLeft} {trainerPhase.barsLeft === 1 ? 'bar' : 'bar'}
              </>
            )}
          </div>
        )}

        <p className="text-xs text-white/40 mt-3 leading-relaxed">
          Esercizio per interiorizzare il polyritmo: voce B suona per <span className="text-pink-300">{trainerPlayBars}</span> bar,
          poi viene mutata per <span className="text-amber-300">{trainerMuteBars}</span> bar. Devi mantenere mentalmente la pulsazione,
          poi voce B torna per verificare se sei rimasto in tempo.
        </p>
      </div>
    </section>
  );
}
